/* byChapters — My Shelf.
 * Wishlist + build status: kept in this browser (localStorage, shelf state scoped per customer) and merged with the
 * server build log (customer metafield chapters.build_log, rendered as data-server-* attributes).
 * Chapter Points + referral link: supplied by the loyalty adapter (snippets/bc-loyalty-adapter.liquid) via window.bcShelf.
 * Completed chapters are sent to the reviews provider (Judge.me). The `bc:chapter-complete` event is for UI only:
 * never award points from it — points must be awarded server-side (loyalty app / review webhooks).
 */
(() => {
  if (window.__bcShelfLoaded) return;
  window.__bcShelfLoaded = true;

  const cfg = window.bcShelfConfig || {};
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const safeUrl = (u) => (/^\/(?!\/)/.test(u || '') ? u : '#');
  const fmt = (n) => Number(n || 0).toLocaleString('en-GB');

  const root = () => $('[data-bc-shelf]');
  const isDemo = () => { const r = root(); return !!(r && r.dataset.demo === 'true'); };
  const KEYS = {
    wish: 'bc:wishlist',
    shelf: () => 'bc:shelf:' + ((root() && root().dataset.customerId) || 'anon'),
  };

  /* Storage (in-memory in demo mode so previews never write to the browser) */
  const memory = {};
  const read = (k, d) => {
    if (isDemo()) return k in memory ? memory[k] : d;
    try { const v = JSON.parse(localStorage.getItem(k)); return v == null ? d : v; } catch (e) { return d; }
  };
  const write = (k, v) => {
    if (isDemo()) { memory[k] = v; return; }
    try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) {}
  };
  const wishlist = () => { const v = read(KEYS.wish, []); return Array.isArray(v) ? v.filter((i) => i && i.handle) : []; };
  const shelfState = () => { const v = read(KEYS.shelf(), {}); return v && typeof v === 'object' && !Array.isArray(v) ? v : {}; };

  function toast(msg) {
    const t = $('[data-toast]');
    if (!t) return;
    t.textContent = msg;
    t.classList.add('is-on');
    clearTimeout(toast._t);
    toast._t = setTimeout(() => t.classList.remove('is-on'), 2600);
  }

  /* ---------- Wishlist buttons (product pages) ---------- */
  function syncWishButtons() {
    const list = wishlist();
    $$('[data-bc-wish]').forEach((btn) => {
      const on = list.some((i) => i.handle === btn.dataset.handle);
      btn.setAttribute('aria-pressed', String(on));
      const label = $('[data-label]', btn);
      if (label) label.textContent = on ? btn.dataset.on : btn.dataset.off;
    });
  }
  function toggleWish(d) {
    let list = wishlist();
    if (list.some((i) => i.handle === d.handle)) list = list.filter((i) => i.handle !== d.handle);
    else list.unshift({ handle: d.handle, title: d.title, url: d.url, image: d.image, price: d.price, world: d.world || '', added: Date.now() });
    write(KEYS.wish, list.slice(0, 60));
  }

  /* ---------- Dashboard ---------- */
  function itemState(el) {
    const s = shelfState()[el.dataset.handle] || {};
    return {
      completed: el.dataset.serverCompleted === 'true' || !!s.completed,
      local: !!s.completed && el.dataset.serverCompleted !== 'true',
      photo: el.dataset.serverPhoto === 'true' || !!s.photo,
      night: el.dataset.serverNight === 'true' || !!s.night,
      reviewed: !!s.reviewed,
    };
  }

  const STATUS_LABEL = { arriving: 'On its way', building: 'Building', completed: 'Completed' };

  function renderDashboard() {
    const r = root();
    if (!r) return;
    const items = $$('[data-shelf-item]', r);
    const owned = new Set(items.map((el) => el.dataset.handle));
    let completed = 0, building = 0, arriving = 0, hours = 0, ahead = 0, night = 0, photos = 0;
    let firstBuilding = null;

    items.forEach((el) => {
      const st = itemState(el);
      let status = el.dataset.base;
      if (st.completed && status !== 'arriving') status = 'completed';
      el.dataset.status = status;
      $$('[data-status-label]', el).forEach((n) => { n.textContent = STATUS_LABEL[status]; });
      $$('[data-status-sr]', el).forEach((n) => { n.textContent = ` (${STATUS_LABEL[status]})`; });
      const h = Number(el.dataset.hours) || 0;
      if (status === 'completed') {
        completed++; hours += h;
        if (st.photo) photos++;
        if (st.night) night++;
      } else {
        ahead += h;
      }
      if (status === 'building') { building++; if (!firstBuilding) firstBuilding = el; }
      if (status === 'arriving') arriving++;
      const pf = $('[data-photo-flag]', el); if (pf) pf.hidden = !st.photo;
      const pb = $('[data-shelf-action="photo"]', el); if (pb) pb.hidden = st.photo || st.reviewed;
      const ub = $('[data-shelf-action="undo"]', el); if (ub) ub.hidden = !st.local;
    });

    /* Wishlist cards */
    const wishWrap = $('[data-wishlist-items]', r);
    const demoWish = $$('[data-demo-wish]', r).length;
    const wishes = wishlist().filter((w) => !owned.has(w.handle));
    if (wishWrap) {
      wishWrap.innerHTML = wishes.map((w) => {
        const url = esc(safeUrl(w.url));
        const img = /^(https?:)?\/\//.test(w.image || '') ? esc(w.image) : '';
        return `
        <article class="bcs-card" data-status="wishlist" data-filter-item>
          <a class="bcs-card__media" href="${url}" tabindex="-1" aria-hidden="true">${img ? `<img src="${img}" alt="" loading="lazy" width="400" height="400">` : ''}<span class="bcs-chip">Wishlist</span></a>
          <div class="bcs-card__plank" aria-hidden="true"></div>
          <div class="bcs-card__body">
            ${w.world ? `<p class="bcs-card__world"><span>${esc(w.world)}</span></p>` : ''}
            <h3 class="bcs-card__title"><a href="${url}">${esc(w.title)}<span class="visually-hidden"> (Wishlist)</span></a></h3>
            ${w.price ? `<p class="bcs-card__meta">${esc(w.price)}</p>` : ''}
            <div class="bcs-card__acts bcs-acts--wishlist">
              <a class="bcs-btn bcs-btn--sm" href="${url}">View world</a>
              <button type="button" class="bcs-link bcs-link--muted" data-wish-remove="${esc(w.handle)}">Remove</button>
            </div>
          </div>
        </article>`;
      }).join('');
    }
    const wishCount = wishes.length + demoWish;

    /* Stats */
    const set = (sel, val) => $$(sel, r).forEach((n) => { n.textContent = val; });
    set('[data-stat="worlds"]', owned.size);
    set('[data-stat="worlds-label"]', owned.size === 1 ? 'World' : 'Worlds');
    set('[data-stat="completed"]', completed);
    set('[data-stat="building"]', building);
    set('[data-stat="arriving"]', arriving);
    set('[data-stat="wishlist"]', wishCount);
    const hl = $('[data-hours-line]', r);
    if (hl) {
      if (hours > 0 || ahead === 0) hl.innerHTML = `<b data-stat="hours">${hours}</b> ${hours === 1 ? 'hour' : 'hours'} built`;
      else hl.innerHTML = `<b data-stat="hours">${ahead}</b> hours of building ahead`;
    }
    $$('[data-count-for]', r).forEach((n) => {
      const f = n.dataset.countFor;
      n.textContent = { all: owned.size + wishCount, wishlist: wishCount, completed, building: building + arriving }[f] ?? '';
    });
    const wn = $('[data-wish-note]', r); if (wn) wn.hidden = !(wishes.length && !isDemo());

    /* Milestones */
    const metrics = { completed, owned: owned.size, night, photos };
    let unlocked = 0;
    $$('[data-milestone]', r).forEach((m) => {
      const have = metrics[m.dataset.rule] || 0;
      const need = Number(m.dataset.threshold) || 1;
      const done = have >= need;
      if (done) unlocked++;
      m.classList.toggle('is-unlocked', done);
      const p = $('[data-milestone-progress]', m);
      if (p) {
        if (!p.dataset.reward) p.dataset.reward = p.textContent;
        p.textContent = done ? 'Unlocked' : (need > 1 ? `${Math.min(have, need)} of ${need}` : p.dataset.reward);
      }
      const bar = $('[data-milestone-bar]', m);
      if (bar) bar.style.width = Math.min(100, (have / need) * 100) + '%';
    });
    set('[data-ms-count]', unlocked);

    /* Complete-your-chapter prompt: the first world still being built */
    const prompt = $('[data-cyc-prompt]', r);
    if (prompt) {
      prompt.hidden = !firstBuilding;
      if (firstBuilding) {
        prompt.dataset.handle = firstBuilding.dataset.handle;
        set('[data-cyc-title]', firstBuilding.dataset.title);
      }
    }

    applyFilter(r);
    renderPoints(r);
  }

  function applyFilter(r) {
    const active = $('[data-shelf-filter][aria-pressed="true"]', r);
    const f = active ? active.dataset.shelfFilter : 'all';
    let visible = 0;
    $$('[data-shelf-item], [data-filter-item]', r).forEach((el) => {
      const s = el.dataset.status;
      const show = f === 'all' || s === f || (f === 'building' && s === 'arriving');
      el.hidden = !show;
      if (show) visible++;
    });
    const empty = $('[data-shelf-empty]', r);
    if (empty) {
      empty.hidden = visible > 0;
      const t = $('[data-empty-title]', empty), x = $('[data-empty-text]', empty);
      const copy = {
        all: ['Your shelf is ready for its first world', 'Every kit you order lands here, so you can track it, build it and collect it.'],
        building: ['Nothing on the workbench', 'When a kit is on its way or arrives, it shows here until you complete your chapter.'],
        completed: ['No completed chapters yet', 'Finished a build? Tap "Complete your chapter" on it to add it here.'],
        wishlist: ['Your wishlist is empty', 'Tap the heart on any kit to save it for later.'],
      }[f];
      if (t && copy) { t.textContent = copy[0]; x.textContent = copy[1]; }
    }
  }

  /* ---------- Chapter Points ---------- */
  let livePoints = null;
  /* The loyalty app's floating launcher (same selector as snippets/bc-loyalty-adapter.liquid). */
  const LOYALTY_LAUNCHER = '#appstle-loyalty-button, button.appstle-loyalty-button, [id*="appstle"] button, [class*="appstle-loyalty"] button';
  function renderPoints(r) {
    r = r || root();
    if (!r) return;
    const el = $('[data-points]', r);
    if (!el) return;
    const balance = livePoints != null ? livePoints : Number(el.dataset.points) || 0;
    const per = Number(r.dataset.pointsPer10) || 1000;
    $$('[data-points-balance]', r).forEach((n) => { n.textContent = fmt(balance); });

    const rewards = $$('[data-reward]', r);
    const costs = rewards.map((x) => Number(x.dataset.cost) || 0).filter((c) => c > 0);
    const best = costs.filter((c) => c <= balance).sort((a, b) => b - a)[0] || 0;
    let next = null;
    rewards.forEach((x) => {
      const cost = Number(x.dataset.cost) || 0;
      const ok = cost > 0 && balance >= cost;
      x.classList.toggle('is-available', ok);
      const s = $('[data-reward-state]', x);
      if (s) s.textContent = ok ? 'Ready to redeem' : `${fmt(cost - balance)} pts to go`;
      if (!ok && cost > 0 && (next == null || cost < next)) next = cost;
    });
    $$('[data-points-worth]', r).forEach((n) => {
      n.textContent = best ? `£${fmt(Math.floor((best / per) * 10))} ready to use` : (next ? `Next reward at ${fmt(next)} pts` : '');
    });
    const bar = $('[data-points-bar]', r), lbl = $('[data-points-next]', r);
    if (bar) {
      if (!costs.length) { bar.style.width = '0'; if (lbl) lbl.textContent = ''; return; }
      const target = next || per;
      bar.style.width = next ? Math.max(4, ((balance - best) / (target - best)) * 100) + '%' : '100%';
      if (lbl) lbl.textContent = next ? `${fmt(next - balance)} points until your next reward` : 'Every reward is unlocked. Treat yourself.';
    }
  }

  /* ---------- Complete your chapter ---------- */
  const MAX_FILES = 5, MAX_BYTES = 10 * 1024 * 1024;
  let files = [];
  let sending = false;
  let downOnBackdrop = false;

  function showError(msg) {
    const err = $('[data-cyc-error]');
    if (!err) return;
    err.textContent = msg || '';
    err.hidden = !msg;
  }
  function openChapter(handle, focusPhoto) {
    if (sending) return;
    const dlg = $('[data-cyc-dialog]');
    const item = $(`[data-shelf-item][data-handle="${CSS.escape(handle)}"]`);
    if (!dlg || !item) return;
    const form = $('[data-cyc-form]', dlg);
    form.reset(); files = []; renderThumbs();
    form.hidden = false; $('[data-cyc-thanks]', dlg).hidden = true; showError('');
    form.elements.handle.value = handle;
    form.elements.product_id.value = item.dataset.productId || '';
    $('[data-dlg-title]', dlg).textContent = item.dataset.title;
    const img = $('[data-dlg-img]', dlg);
    img.src = item.dataset.image || ''; img.hidden = !item.dataset.image;
    dlg.dataset.handle = handle;
    if (typeof dlg.showModal === 'function') dlg.showModal(); else dlg.setAttribute('open', '');
    delete dlg.dataset.resendOk;
    const st0 = shelfState()[handle] || {};
    if ((st0.reviewed || st0.reviewSent) && !isDemo()) showError('You’ve already sent a review for this kit. Thank you! It can take up to three days to appear. Sending again may create a duplicate.');
    if (focusPhoto) { const fi = $('input[type="file"]', dlg); if (fi) fi.focus(); }
  }
  function closeChapter() {
    if (sending) return;
    const dlg = $('[data-cyc-dialog]');
    if (!dlg) return;
    if (typeof dlg.close === 'function') dlg.close(); else dlg.removeAttribute('open');
  }
  function renderThumbs() {
    const wrap = $('[data-thumbs]');
    if (!wrap) return;
    $$('img', wrap).forEach((i) => URL.revokeObjectURL(i.src));
    wrap.innerHTML = '';
    files.forEach((f) => {
      const i = document.createElement('img');
      i.alt = ''; i.src = URL.createObjectURL(f);
      wrap.appendChild(i);
    });
  }
  function addFiles(list) {
    showError('');
    let skipped = 0, tooBig = '';
    [...list].forEach((f) => {
      if (!/^image\/(jpeg|png|webp)$/.test(f.type)) { skipped++; return; }
      if (f.size > MAX_BYTES) { tooBig = f.name; return; }
      if (files.length < MAX_FILES) files.push(f); else skipped++;
    });
    renderThumbs();
    if (tooBig) showError(`${tooBig} is over 10MB. Please choose a smaller photo.`);
    else if (skipped) showError(`Up to ${MAX_FILES} photos, as JPG or PNG. ${skipped === 1 ? 'One file was' : skipped + ' files were'} left out.`);
  }

  function markComplete(handle, extra = {}) {
    const state = shelfState();
    const prev = state[handle] || {};
    state[handle] = Object.assign(prev, { completed: prev.completed || Date.now() }, extra);
    write(KEYS.shelf(), state);
    renderDashboard();
  }

  async function submitChapter(form) {
    if (sending) return;
    const dlg = $('[data-cyc-dialog]');
    const btn = $('[data-cyc-submit]', dlg);
    const data = {
      handle: form.elements.handle.value,
      productId: form.elements.product_id.value,
      rating: Number((form.querySelector('input[name="rating"]:checked') || {}).value || 0),
      difficulty: Number((form.querySelector('input[name="difficulty"]:checked') || {}).value || 0),
      experience: Number((form.querySelector('input[name="experience"]:checked') || {}).value || 0),
      title: form.elements.title.value.trim(),
      body: form.elements.body.value.trim(),
      night: form.elements.night.checked,
      photos: files.slice(),
      name: dlg.dataset.name,
      email: dlg.dataset.email,
      shop: dlg.dataset.shop,
      customerId: dlg.dataset.customerId || (window.__st && window.__st.cid) || '',
    };
    const toReviews = cfg.reviews === 'judgeme' && !!window.bcShelf.providers.judgeme;
    const reviewing = data.body.length > 0 || data.rating > 0 || data.photos.length > 0;
    if (!reviewing) { showError('Add a photo or a review, or choose "Just mark it as complete".'); return; }
    if (toReviews && (!data.rating || data.body.length < 10)) {
      showError(!data.rating
        ? 'Please give your chapter a star rating. Your photos are posted with your review.'
        : 'Please write a few words (10 characters or more) to go with your review.');
      return;
    }
    const prev = shelfState()[data.handle] || {};
    /* A sent review is not proof it arrived (see judgemeSubmit), so never lock the customer out: warn once, then let them send. */
    if (toReviews && (prev.reviewed || prev.reviewSent) && !isDemo() && dlg.dataset.resendOk !== '1') {
      dlg.dataset.resendOk = '1';
      showError('You’ve already sent a review for this kit, and sending again may create a duplicate. Press Complete my chapter again to send it anyway.');
      return;
    }
    showError('');
    sending = true;
    btn.disabled = true; btn.textContent = 'Sending…';
    let sent = true;
    let verified = false;
    try {
      if (!isDemo() && toReviews) verified = !!((await window.bcShelf.providers.judgeme(data)) || {}).verified;
    } catch (e) {
      sent = false;
      showError(e && e.message === 'decode'
        ? 'We couldn’t read one of your photos. Please choose a JPG or PNG and try again.'
        : 'Sorry, we couldn’t send that just now. Please try again, or send your photos through the chat instead.');
    } finally {
      sending = false;
      btn.disabled = false; btn.textContent = 'Complete my chapter';
    }
    if (!sent) return;

    const hasPhoto = data.photos.length > 0;
    markComplete(data.handle, { photo: hasPhoto || undefined, night: (data.night && hasPhoto) || undefined, reviewed: (toReviews && !isDemo() && verified) || undefined, reviewSent: (toReviews && !isDemo()) || undefined, sentAt: Date.now() });
    document.dispatchEvent(new CustomEvent('bc:chapter-complete', { detail: { handle: data.handle, productId: data.productId, rating: data.rating, photos: data.photos.length } }));
    form.hidden = true;
    const thanks = $('[data-cyc-thanks]', dlg);
    const tt = $('[data-thanks-text]', thanks);
    if (tt) {
      if (!toReviews) tt.textContent = 'Saved to your shelf. Nicely built!';
      else if (!cfg.loyaltyLive) tt.textContent = 'Thank you for sharing your build. It’s with our team to check, and it will appear on the kit’s page once it’s approved.';
      else tt.textContent = hasPhoto
        ? 'Thank you for sharing your build. It’s with our team to check, and your Chapter Points are added once it’s approved.'
        : 'Thank you. Your review is with our team to check, and your Chapter Points are added once it’s published.';
    }
    thanks.hidden = false;
    const th = $('[data-thanks-h]', thanks); if (th) th.focus();
  }

  /* ---------- Referral sharing ---------- */
  function referralUrl() { const i = $('#bcs-ref-link'); return i ? i.value : ''; }
  function wireShare(url) {
    const text = (cfg.shareText || 'Here’s £10 off your first byChapters kit:') + ' ' + url;
    const wa = $('[data-share-wa]'); if (wa) wa.href = 'https://wa.me/?text=' + encodeURIComponent(text);
    const ml = $('[data-share-mail]'); if (ml) ml.href = 'mailto:?subject=' + encodeURIComponent('£10 off byChapters') + '&body=' + encodeURIComponent(text);
    const sh = $('[data-share]'); if (sh) sh.hidden = !navigator.share;
  }

  /* ---------- Events ---------- */
  document.addEventListener('pointerdown', (e) => { downOnBackdrop = !!(e.target.matches && e.target.matches('[data-cyc-dialog]')); });
  document.addEventListener('click', (e) => {
    const t = e.target;
    const wishBtn = t.closest('[data-bc-wish]');
    if (wishBtn) { e.preventDefault(); toggleWish(wishBtn.dataset); syncWishButtons(); renderDashboard(); return; }
    const rm = t.closest('[data-wish-remove]');
    if (rm) { write(KEYS.wish, wishlist().filter((i) => i.handle !== rm.dataset.wishRemove)); syncWishButtons(); renderDashboard(); toast('Removed from your wishlist'); return; }
    const filter = t.closest('[data-shelf-filter]');
    if (filter) {
      $$('[data-shelf-filter]', filter.closest('.bcs-filters')).forEach((b) => b.setAttribute('aria-pressed', String(b === filter)));
      applyFilter(root()); return;
    }
    const act = t.closest('[data-shelf-action]');
    if (act) {
      const item = act.closest('[data-shelf-item]');
      const h = item.dataset.handle;
      if (act.dataset.shelfAction === 'complete') openChapter(h);
      if (act.dataset.shelfAction === 'photo') openChapter(h, true);
      if (act.dataset.shelfAction === 'undo') {
        const state = shelfState(); if (state[h]) delete state[h].completed; write(KEYS.shelf(), state); renderDashboard();
      }
      return;
    }
    if (t.closest('[data-cyc-open]')) { const p = t.closest('[data-cyc-prompt]'); if (p && p.dataset.handle) openChapter(p.dataset.handle); return; }
    if (t.closest('[data-dlg-close]')) { closeChapter(); return; }
    if (t.closest('[data-cyc-skip]')) {
      if (sending) return;
      const dlg = $('[data-cyc-dialog]'); markComplete(dlg.dataset.handle); closeChapter(); toast('Chapter complete. Nicely built!'); return;
    }
    if (t.matches('[data-cyc-dialog]') && downOnBackdrop) { closeChapter(); return; } /* backdrop click */
    if (t.closest('[data-redeem]')) {
      if (typeof window.bcShelf.onRedeem === 'function') { window.bcShelf.onRedeem(); return; }
      if (!cfg.loyaltyLive) { toast('Redeeming opens when Chapter Points launch'); return; }
      /* No loyalty adapter on the page (e.g. a member with no points record yet): open the app's launcher, else its page. */
      const launcher = $(LOYALTY_LAUNCHER);
      if (launcher) launcher.click(); else window.location.href = '/pages/chapter-points#earn';
      return;
    }
    const copy = t.closest('[data-copy]');
    if (copy) {
      const input = document.getElementById(copy.dataset.copy);
      if (!input || !input.value) return;
      const done = () => { toast('Link copied'); };
      if (navigator.clipboard) navigator.clipboard.writeText(input.value).then(done, () => input.select());
      else { input.select(); try { document.execCommand('copy'); done(); } catch (err) {} }
      return;
    }
    if (t.closest('[data-share]')) {
      const url = referralUrl();
      if (url && navigator.share) navigator.share({ title: '£10 off byChapters', text: cfg.shareText || '', url }).catch(() => {});
    }
  });
  /* After the dialog closes, return focus to something still visible on that card. */
  document.addEventListener('close', (e) => {
    if (!e.target.matches || !e.target.matches('[data-cyc-dialog]')) return;
    const h = e.target.dataset.handle;
    const card = h && $(`[data-shelf-item][data-handle="${CSS.escape(h)}"]`);
    if (card) setTimeout(() => { const a = $('.bcs-card__title a', card); if (a) a.focus(); }, 0);
  }, true);
  document.addEventListener('cancel', (e) => { if (sending && e.target.matches && e.target.matches('[data-cyc-dialog]')) e.preventDefault(); }, true);
  document.addEventListener('change', (e) => {
    if (e.target.matches('[data-cyc-dialog] input[type="file"]')) { addFiles(e.target.files); e.target.value = ''; }
  });
  document.addEventListener('submit', (e) => {
    if (e.target.matches('[data-cyc-form]')) { e.preventDefault(); submitChapter(e.target); }
  });
  ['dragover', 'dragleave', 'drop'].forEach((type) => document.addEventListener(type, (e) => {
    const drop = e.target.closest && e.target.closest('[data-drop]');
    if (!drop) return;
    e.preventDefault();
    drop.classList.toggle('is-over', type === 'dragover');
    if (type === 'drop' && e.dataTransfer) addFiles(e.dataTransfer.files);
  }));
  window.addEventListener('storage', (e) => { if (e.key === KEYS.wish || e.key === KEYS.shelf()) { syncWishButtons(); renderDashboard(); } });

  /* ---------- Public API for the loyalty + reviews adapters ---------- */
  const queued = (window.bcShelf && Array.isArray(window.bcShelf.q)) ? window.bcShelf.q : [];
  window.bcShelf = {
    providers: {},
    onRedeem: null,
    setPoints(n) { livePoints = Number(n) || 0; const p = $('[data-points-pending]'); if (p) p.hidden = true; renderPoints(); },
    setReferral(url) {
      if (!url) return;
      $$('[data-referral]').forEach((wrap) => { const input = $('input', wrap); if (input) input.value = url; wrap.hidden = false; });
      $$('[data-referral-pending]').forEach((n) => { n.hidden = true; });
      wireShare(url);
    },
    markEarned(key) { const li = $(`[data-earn="${key}"]`); if (li) li.classList.add('is-done'); },
    wishlist,
    refresh() { syncWishButtons(); renderDashboard(); },
  };

  /* ---------- Judge.me: photos via its presigned upload, review via its public endpoint ---------- */
  const JM = 'https://api.judge.me/api/v1';
  const DIFF = ['Relaxing', 'Easy-going', 'Just right', 'Challenging', 'Epic'];
  const EXP = ['Frustrating', 'Okay', 'Enjoyable', 'Loved it', 'Unforgettable'];
  const withTimeout = (ms) => { const c = new AbortController(); setTimeout(() => c.abort(), ms); return c.signal; };
  function toJpeg(file) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        const max = 2400, k = Math.min(1, max / Math.max(img.naturalWidth, img.naturalHeight));
        const c = document.createElement('canvas');
        c.width = Math.round(img.naturalWidth * k); c.height = Math.round(img.naturalHeight * k);
        c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
        c.toBlob((b) => { URL.revokeObjectURL(url); if (b) resolve(b); else reject(new Error('decode')); }, 'image/jpeg', 0.85);
      };
      img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('decode')); };
      img.src = url;
    });
  }
  async function jmUpload(file, shop, i) {
    const q = `url=${encodeURIComponent(shop)}&shop_domain=${encodeURIComponent(shop)}&platform=shopify`;
    const pre = await fetch(`${JM}/pictures/presigned_data?${q}`, { signal: withTimeout(30000) }).then((r) => { if (!r.ok) throw new Error('presign'); return r.json(); });
    const blob = await toJpeg(file);
    if (blob.size > MAX_BYTES) throw new Error('decode');
    const key = `${pre.key_prefix}${Date.now()}_${i}.jpg`;
    const fd = new FormData();
    fd.append('key', key);
    Object.entries(pre.fields || {}).forEach(([k, v]) => fd.append(k, v));
    fd.append('file', blob, `build_${i}.jpg`);
    const res = await fetch(pre.url, { method: 'POST', body: fd, signal: withTimeout(60000) });
    if (!res.ok) throw new Error('upload');
    const m = (await res.text()).match(/<Key>([^<]+)<\/Key>/);
    return m ? m[1] : key;
  }
  async function judgemeSubmit(d) {
    const keys = [];
    for (let i = 0; i < d.photos.length; i++) keys.push(await jmUpload(d.photos[i], d.shop, i));
    const extra = [];
    if (d.difficulty) extra.push(`Difficulty: ${d.difficulty}/5 (${DIFF[d.difficulty - 1]})`);
    if (d.experience) extra.push(`Build experience: ${d.experience}/5 (${EXP[d.experience - 1]})`);
    const fd = new FormData();
    const add = (k, v) => { if (v !== undefined && v !== null && v !== '') fd.append(k, v); };
    add('url', d.shop); add('shop_domain', d.shop); add('platform', 'shopify');
    add('id', d.productId); add('reviewed_product_ids', d.productId);
    add('name', d.name || 'byChapters builder'); add('email', d.email);
    add('reviewer_name_format', 'last_initial');
    add('rating', d.rating); add('title', d.title);
    add('body', extra.length ? `${d.body}\n\n${extra.join(' · ')}` : d.body);
    add('shopify_customer_id', d.customerId);
    keys.forEach((k, i) => add(`picture_keys[${i}]`, k));
    /* api.judge.me answers with access-control-allow-origin: *, so the result is readable: a refusal throws and the form
       stays open with its error. Only if the browser blocks the readable request (TypeError) do we send once more as a
       simple no-cors request, whose result cannot be read ("sent, not verified"). A server-side proxy is the post-launch step. */
    let res;
    try {
      res = await fetch(`${JM}/reviews`, { method: 'POST', body: fd, signal: withTimeout(30000) });
    } catch (e) {
      if (!(e instanceof TypeError)) throw e;
      await fetch(`${JM}/reviews`, { method: 'POST', body: fd, mode: 'no-cors', signal: withTimeout(30000) });
      return { verified: false };
    }
    if (!res.ok) throw new Error(`review ${res.status}`);
    return { verified: true };
  }
  window.bcShelf.providers.judgeme = judgemeSubmit;

  queued.forEach((call) => { try { const [m, ...a] = call; if (typeof window.bcShelf[m] === 'function') window.bcShelf[m](...a); } catch (e) {} });

  const init = () => {
    Object.assign(cfg, window.bcShelfConfig || {});
    syncWishButtons();
    if (isDemo()) window.bcShelf.setReferral(location.origin + '/?ref=ROSIE10');
    renderDashboard();
    document.dispatchEvent(new CustomEvent('bc:shelf-ready'));
  };
  document.addEventListener('shopify:section:load', init);
  window.addEventListener('pageshow', (e) => { if (e.persisted) init(); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();

/* Share your shelf (3 Oct 2026): the wishlist travels as a plain link (?wish=handle,handle) so friends can see
 * the kits someone is hoping for and buy one as a present. Nothing personal is in the link — just kit handles —
 * and the shared view renders for anyone, signed in or not, from /products/<handle>.js. */
(function () {
  const $ = (s, r) => (r || document).querySelector(s);
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const money = (p) => '£' + (p / 100).toFixed(2).replace(/\.00$/, '');
  const wishlist = () => {
    try { const v = JSON.parse(localStorage.getItem('bc:wishlist')); return Array.isArray(v) ? v.filter((i) => i && i.handle) : []; } catch (e) { return []; }
  };

  function initShare() {
    const row = $('[data-share-row]'), btn = $('[data-share-shelf]');
    if (!row || !btn || btn.hasAttribute('data-ready')) return;
    btn.setAttribute('data-ready', '');
    const sync = () => { row.hidden = wishlist().length === 0; };
    sync();
    window.addEventListener('storage', sync);
    document.addEventListener('click', (e) => { if (e.target.closest('[data-bc-wish],[data-wish-remove]')) setTimeout(sync, 50); });
    btn.addEventListener('click', async () => {
      const handles = wishlist().map((w) => w.handle).slice(0, 40);
      if (!handles.length) return;
      const url = location.origin + '/pages/my-shelf?wish=' + handles.map(encodeURIComponent).join(',');
      const done = $('[data-share-done]'), hint = $('[data-share-hint]');
      if (navigator.share) {
        try { await navigator.share({ title: 'My byChapters shelf', text: 'The kits on my shelf — in case you ever need present ideas.', url }); return; } catch (e) { if (e && e.name === 'AbortError') return; }
      }
      try {
        await navigator.clipboard.writeText(url);
        if (done) { done.hidden = false; if (hint) hint.hidden = true; setTimeout(() => { done.hidden = true; if (hint) hint.hidden = false; }, 4000); }
      } catch (e) {
        window.prompt('Copy your shelf link:', url);
      }
    });
  }

  function initFriend() {
    const panel = $('[data-friend-shelf]'), grid = $('[data-friend-grid]');
    if (!panel || !grid || panel.hasAttribute('data-ready')) return;
    const raw = new URLSearchParams(location.search).get('wish');
    if (!raw) return;
    panel.setAttribute('data-ready', '');
    const handles = raw.split(',').map((h) => h.trim().toLowerCase()).filter((h) => /^[a-z0-9-]{1,120}$/.test(h)).slice(0, 40);
    if (!handles.length) return;
    panel.hidden = false;
    Promise.all(handles.map((h) =>
      fetch('/products/' + h + '.js').then((r) => (r.ok ? r.json() : null)).catch(() => null)
    )).then((list) => {
      const ok = list.filter(Boolean);
      if (!ok.length) { const err = $('[data-friend-error]'); if (err) err.hidden = false; return; }
      grid.innerHTML = ok.map((p) => {
        const img = p.featured_image ? esc(p.featured_image) + (p.featured_image.indexOf('?') > -1 ? '&' : '?') + 'width=400' : '';
        return `
        <article class="bcs-card" data-status="wishlist">
          <a class="bcs-card__media" href="/products/${esc(p.handle)}" tabindex="-1" aria-hidden="true">${img ? `<img src="${img}" alt="" loading="lazy" width="400" height="400">` : ''}<span class="bcs-chip">On their shelf</span></a>
          <div class="bcs-card__plank" aria-hidden="true"></div>
          <div class="bcs-card__body">
            <h3 class="bcs-card__title"><a href="/products/${esc(p.handle)}">${esc(p.title)}</a></h3>
            <p class="bcs-card__meta">${money(p.price)}${p.available ? '' : ' · currently sold out'}</p>
            <div class="bcs-card__acts"><a class="bcs-btn bcs-btn--sm" href="/products/${esc(p.handle)}">View this kit</a></div>
          </div>
        </article>`;
      }).join('');
    });
  }

  const boot = () => { initShare(); initFriend(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
})();
